// The pure half of the notification screens: which catalog words name a kind, a channel, a source
// and an audience, and which writes a gesture becomes. The screens live in the settings (the
// caller's notifications, the organization's defaults), on the workspace screen ("Follow this
// workspace") and in a channel's monetization settings (« Me prévenir de chaque vente »); what they
// share is here, so each answer is given once and tested once.

import { ApiError } from '@/api/client'
import type { NotificationAudience, NotificationChannelDefault } from '@/api/notifications/defaults'
import type {
  NotificationChannel,
  NotificationChannelPreference,
  NotificationChoiceChange,
  NotificationKind,
  NotificationSource,
} from '@/api/notifications/preferences'
import { OrgPermissions, WorkspacePermissions, type OrgPermission, type WorkspacePermission } from '@/types/permissions'

// --------------------------------------------------------------------------- catalog words ----

/**
 * The catalog segment each kind's words sit under. A kind's key carries dots, which vue-i18n reads
 * as nesting, so the catalogs key on these instead; `satisfies` makes a kind added to the contract
 * without its words a type error.
 */
const KIND_COPY = new Map<string, string>(
  Object.entries({
    'assets.upload_received': 'uploadReceived',
    'assets.upload_pending_moderation': 'uploadPendingModeration',
    'tenancy.membership_request_received': 'membershipRequestReceived',
    'tenancy.membership_requests_waiting': 'membershipRequestsWaiting',
    'websites.sale_received': 'saleReceived',
    'websites.contact_message_received': 'contactMessageReceived',
    'websites.deposit_received': 'depositReceived',
  } satisfies Record<NotificationKind, string>),
)

/**
 * The catalog segment of a kind's words, or `null` for a kind this build does not know — the
 * contract says the list grows, and a newer server may name one before the screen can label it.
 */
export function kindCopy(kind: string): string | null {
  return KIND_COPY.get(kind) ?? null
}

/** The catalog segment of a channel's name. */
export const CHANNEL_COPY: Record<NotificationChannel, string> = {
  email: 'email',
  in_app: 'inApp',
}

/** The catalog segment saying where a value came from. */
export const SOURCE_COPY: Record<NotificationSource, string> = {
  member_scoped: 'memberScoped',
  member: 'member',
  organization: 'organization',
  registry: 'registry',
  none: 'none',
}

/** How a default audience is said, by catalog segment, and the permission it names when no word does. */
export interface AudienceCopy {
  key:
    | 'administrators'
    | 'reviewers'
    | 'holders'
    | 'workspaceSeat'
    | 'workspaceReader'
    | 'moderators'
    | 'workspaceHolders'
    | 'sellers'
    | 'websiteOperator'
  permission: string | null
}

/**
 * The organization permissions a default audience is said in words for. Any other is said as "the
 * members whose role holds it", naming the permission.
 */
const PERMISSION_AUDIENCES = new Map<string, AudienceCopy['key']>(
  Object.entries({
    [OrgPermissions.TENANCY_WORKSPACES_MANAGE_ALL]: 'administrators',
    [OrgPermissions.TENANCY_MEMBERSHIP_REQUESTS_REVIEW]: 'reviewers',
    [OrgPermissions.WEBSITES_STOREFRONT_EDIT]: 'sellers',
  } satisfies Partial<Record<OrgPermission, AudienceCopy['key']>>),
)

/**
 * The workspace permissions a default audience is said in words for. Any other is said as "the
 * members whose role in the workspace holds it", naming the permission.
 */
const WORKSPACE_PERMISSION_AUDIENCES = new Map<string, AudienceCopy['key']>(
  Object.entries({
    [WorkspacePermissions.ASSETS_ASSET_MODERATE]: 'moderators',
  } satisfies Partial<Record<WorkspacePermission, AudienceCopy['key']>>),
)

/** How one shape of a default audience is said. */
export function audienceCopy(audience: NotificationAudience): AudienceCopy {
  switch (audience.shape) {
    case 'workspace_seat':
      return { key: 'workspaceSeat', permission: null }
    case 'workspace_reader':
      return { key: 'workspaceReader', permission: null }
    case 'website_operator':
      return { key: 'websiteOperator', permission: null }
    case 'workspace_holding': {
      const permission = audience.permission ?? ''
      return { key: WORKSPACE_PERMISSION_AUDIENCES.get(permission) ?? 'workspaceHolders', permission }
    }
    case 'org_holding': {
      const permission = audience.permission ?? ''
      return { key: PERMISSION_AUDIENCES.get(permission) ?? 'holders', permission }
    }
  }
}

// ------------------------------------------------------------------- the caller's choices ----

/** What a kind tells the screen about its obligations: only a member of its default audience is bound. */
interface Bound {
  mandatory: boolean
}

/**
 * Whether a channel's switch is out of the caller's hands: a channel that cannot be written yet, or
 * the mail of a kind the platform owes them — which reads on, and stays on.
 */
export function channelLocked(kind: Bound, channel: NotificationChannelPreference): boolean {
  return !channel.selectable || (kind.mandatory && channel.channel === 'email')
}

/** Whether the caller's own organization-wide choice is what decides, so it can be cleared. */
export function canClearChoice(channel: NotificationChannelPreference): boolean {
  return channel.selectable && channel.source === 'member'
}

/** One kind on one channel, set or cleared. */
export function choice(
  kind: NotificationKind,
  channel: NotificationChannel,
  enabled: boolean | null,
): NotificationChoiceChange {
  return { kind, channel, enabled }
}

/** Why a write was refused, by catalog segment. */
export type ChoiceRefusal = 'mandatory' | 'notEligible' | 'notSelectable' | 'failed'

/**
 * Why a write of choices was refused: mail turned off on a kind the platform owes (`422`), a kind
 * the caller may no longer be told of (`403`), a channel that cannot be written yet (`400`), or
 * anything else.
 */
export function choiceRefusal(error: unknown): ChoiceRefusal {
  if (!(error instanceof ApiError)) return 'failed'
  if (error.status === 422) return 'mandatory'
  if (error.code === 'forbidden') return 'notEligible'
  if (error.status === 400) return 'notSelectable'
  return 'failed'
}

// ---------------------------------------------------------- following a workspace or a channel ----

/**
 * One kind as the caller holds it in one place — a workspace, or one of the organization's
 * channels. The two reads answer the same fields, and following either is the same gesture: a
 * choice for that place alone, which wins over the caller's organization-wide one.
 */
export interface ScopedKindPreference {
  kind: NotificationKind
  mandatory: boolean
  channels: NotificationChannelPreference[]
}

/** The channels the caller can write for a kind here. */
function writable(kind: ScopedKindPreference): NotificationChannelPreference[] {
  return kind.channels.filter((channel) => channel.selectable)
}

/** Whether the caller follows a kind here: some channel they can write is on here. */
export function isFollowed(kind: ScopedKindPreference): boolean {
  return writable(kind).some((channel) => channel.enabled)
}

/**
 * Whether the follow switch does anything: some channel can be written, and it is not a mandatory
 * kind's mail, which cannot be turned off.
 */
export function canToggleFollow(kind: ScopedKindPreference): boolean {
  return writable(kind).some((channel) => !channelLocked(kind, channel))
}

/**
 * **The first write toward following (or not) a kind here**, leaving as few choices behind as the
 * result allows. Each writable channel not already where it should be is either a choice made for
 * this place, cleared — the organization-wide value may be the one wanted, and then no row is
 * needed — or it already follows that value, and is written for this place. A mandatory kind's mail
 * is left alone.
 */
export function followChanges(kind: ScopedKindPreference, follow: boolean): NotificationChoiceChange[] {
  return writable(kind)
    .filter((channel) => channel.enabled !== follow && !channelLocked(kind, channel))
    .map((channel) => choice(kind.kind, channel.channel, channel.source === 'member_scoped' ? null : follow))
}

/**
 * **What is still owed once the first write answered**: every writable channel the clear left on the
 * wrong side, written for this place. Empty when clearing was enough.
 */
export function remainingFollowChanges(kind: ScopedKindPreference, follow: boolean): NotificationChoiceChange[] {
  return writable(kind)
    .filter((channel) => channel.enabled !== follow && !channelLocked(kind, channel))
    .map((channel) => choice(kind.kind, channel.channel, follow))
}

/**
 * Where the follow state of a kind comes from, as one source: the first writable channel's. Every
 * channel a follow writes moves together, so they agree but where only part of them were chosen.
 */
export function followSource(kind: ScopedKindPreference): NotificationSource | null {
  return writable(kind)[0]?.source ?? null
}

// ------------------------------------------------------------- the organization's defaults ----

/** The organization's choice for a kind on a channel: the platform's default, on, or off. */
export type DefaultChoice = 'platform' | 'on' | 'off'

/** What the organization set for a channel, as the three-way control says it. */
export function defaultChoice(channel: NotificationChannelDefault): DefaultChoice {
  if (channel.organization === null) return 'platform'
  return channel.organization ? 'on' : 'off'
}

/** The write a three-way control's value becomes: the platform's default is a clear. */
export function defaultChange(
  kind: NotificationKind,
  channel: NotificationChannel,
  value: DefaultChoice,
): NotificationChoiceChange {
  return choice(kind, channel, value === 'platform' ? null : value === 'on')
}
